// Shared Footer Component - Consistent across all pages
import { Link } from "react-router-dom";
import { Phone, Mail, MapPin } from "lucide-react";
import { LotusLogo } from "./SharedComponents";
import { BWYBadgeInline } from "./TrustBadges";

// Facebook icon
const FacebookIcon = ({ className = "w-5 h-5" }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
  </svg>
);

// Instagram icon
const InstagramIcon = ({ className = "w-5 h-5" }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
  </svg>
);

// YouTube icon
const YouTubeIcon = ({ className = "w-5 h-5" }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
  </svg>
);

const Footer = () => (
  <footer data-testid="footer" className="py-12 px-6 bg-neutral-900 text-white">
    <div className="max-w-6xl mx-auto">
      <div className="grid md:grid-cols-4 gap-10 mb-10">
        <div className="md:col-span-1">
          <div className="flex items-center gap-3 mb-4">
            <LotusLogo className="w-10 h-10" />
            <span className="font-heading text-xl">Annelie Lily</span>
          </div>
          <p className="text-neutral-400 text-sm leading-relaxed mb-4">
            Yoga Teacher & Holistic Healer in Frome, Somerset. 
            Yoga classes, treatments, women's circles and retreats.
          </p>
          {/* Social Links */}
          <div className="flex items-center gap-4 mb-4">
            <a
              href="https://www.facebook.com/seventhsensehealing"
              target="_blank"
              rel="noopener noreferrer"
              className="text-neutral-400 hover:text-[#1877F2] transition-colors"
              title="Follow on Facebook"
              data-testid="footer-facebook-link"
            >
              <FacebookIcon />
            </a>
            <a
              href="https://www.instagram.com/seventh_sense_healing"
              target="_blank"
              rel="noopener noreferrer"
              className="text-neutral-400 hover:text-[#E4405F] transition-colors"
              title="Follow on Instagram"
              data-testid="footer-instagram-link"
            >
              <InstagramIcon />
            </a>
            <a
              href="https://www.youtube.com/channel/UClfl6mm-QU-_3pyrXBU6B6g"
              target="_blank"
              rel="noopener noreferrer"
              className="text-neutral-400 hover:text-[#FF0000] transition-colors"
              title="Subscribe on YouTube"
              data-testid="footer-youtube-link"
            >
              <YouTubeIcon />
            </a>
          </div>
          {/* BWY Badge */}
          <BWYBadgeInline className="text-neutral-400" />
        </div>
        
        <div>
          <h4 className="font-medium mb-4 text-sm uppercase tracking-wide text-gold">Offerings</h4>
          <ul className="space-y-2 text-sm text-neutral-400">
            <li><Link to="/yoga-classes" className="hover:text-gold transition-colors">Yoga Classes</Link></li>
            <li><Link to="/treatments" className="hover:text-gold transition-colors">Healing & Treatments</Link></li>
            <li><Link to="/womens-circles" className="hover:text-gold transition-colors">Women's Circles</Link></li>
            <li><Link to="/retreats" className="hover:text-gold transition-colors">Retreats</Link></li>
            <li><Link to="/yoga-classes/private-yoga" className="hover:text-gold transition-colors">Private Sessions</Link></li>
          </ul>
        </div>
        
        <div>
          <h4 className="font-medium mb-4 text-sm uppercase tracking-wide text-gold">Explore</h4>
          <ul className="space-y-2 text-sm text-neutral-400">
            <li><Link to="/about" className="hover:text-gold transition-colors">About Me</Link></li>
            <li><Link to="/yoga-classes/hatha-yoga" className="hover:text-gold transition-colors">Hatha Yoga</Link></li>
            <li><Link to="/yoga-classes/yin-yoga" className="hover:text-gold transition-colors">Yin Yoga</Link></li>
            <li><Link to="/treatments/reiki-healing" className="hover:text-gold transition-colors">Reiki Healing</Link></li>
            <li><Link to="/treatments/chakra-healing" className="hover:text-gold transition-colors">Chakra Healing</Link></li>
            <li><Link to="/areas" className="hover:text-gold transition-colors">My Offering Areas</Link></li>
          </ul>
        </div>
        
        <div>
          <h4 className="font-medium mb-4 text-sm uppercase tracking-wide text-gold">Contact</h4>
          <ul className="space-y-3 text-sm text-neutral-400">
            <li>
              <a href="tel:07540995021" className="hover:text-gold transition-colors flex items-center gap-2">
                <Phone className="w-4 h-4" /> 07540 995021
              </a>
            </li>
            <li>
              <a href="mailto:seventhsensehealing@outlook.com" className="hover:text-gold transition-colors flex items-center gap-2">
                <Mail className="w-4 h-4" /> Email me
              </a>
            </li>
            <li className="flex items-start gap-2">
              <MapPin className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <span>Little Keyford Lane<br />Frome, Somerset BA11 5BA</span>
            </li>
          </ul>
        </div>
      </div>
      
      <div className="border-t border-neutral-800 pt-8 flex flex-col md:flex-row items-center justify-between gap-4">
        <div>
          <p className="text-neutral-500 text-sm">
            © {new Date().getFullYear()} Annelie Lily · Seventh Sense Healing Limited
          </p>
          <p className="text-neutral-600 text-xs mt-1" data-testid="footer-rebrand-signal">
            Annelie Lily — formerly Seventh Sense Healing.
          </p>
        </div>
        <div className="flex flex-wrap items-center justify-center gap-4 text-neutral-500 text-xs">
          <Link to="/terms" className="hover:text-gold transition-colors">Terms & Conditions</Link>
          <span>·</span>
          <Link to="/privacy-policy" className="hover:text-gold transition-colors">Privacy Policy</Link>
          <span>·</span>
          <Link to="/cookie-policy" className="hover:text-gold transition-colors">Cookie Policy</Link>
        </div>
      </div>
      <p className="text-neutral-600 text-xs text-center mt-4">
        <a 
          href="https://bwy.org.uk/" 
          target="_blank" 
          rel="noopener noreferrer"
          className="hover:text-gold transition-colors"
        >
          British Wheel of Yoga
        </a> Certified Teacher · Company No. 10800506
      </p>
    </div>
  </footer>
);

export default Footer;
